/**
 * H①-native — HealthKit daily-aggregate sync (the shell → server bridge).
 *
 * Contract (launch-plan §5 H①-native): read the last 7 days of daily
 * summaries from the local `tayro-healthkit` plugin and upsert them via
 * `POST /api/health-metrics/daily` (self-only; the server re-gates on
 * `health_context_v1` + the minor-consent ladder). Raw samples never
 * touch this layer — the plugin returns aggregates only.
 *
 * Guarded like liveActivity.ts: native-only, best-effort, NEVER throws —
 * a health sync must not break the app. Every failure path resolves
 * silently (permission denied → empty days; feature/consent gate → 403;
 * offline → caught). We bind the plugin by jsName via @capacitor/core so
 * this module (and the web build) never depends on the plugin package's JS.
 */

import { isNativeApp } from '../platform';
import { cleanDaysForUpload, type RawHealthDay } from './normalize';
import type { AuthFetchFn } from '../../hooks/useAuthFetch';

/** The ONE binding for the native HealthKit plugin (H① daily metrics + P4
 *  workout import). Capacitor warns on duplicate `registerPlugin` names, so
 *  every consumer shares this module-level proxy. */
export interface HealthKitPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  requestAuthorization(): Promise<{ granted: boolean }>;
  queryDailyMetrics(options: { days: number }): Promise<{ days: RawHealthDay[] }>;
  // P4 — workout import (separate HK read types, own auth request).
  requestWorkoutAuthorization(): Promise<{ granted: boolean }>;
  queryWorkouts(options: { days: number }): Promise<{ workouts: unknown[] }>;
}

let boundPlugin: HealthKitPlugin | null = null;

/** Bind (once) and return the HealthKit plugin proxy; null off-native or on
 *  bind failure. Wrapped in an object per the Live Activity thenable lesson. */
export async function getHealthKitPlugin(): Promise<{ hk: HealthKitPlugin } | null> {
  if (!isNativeApp()) return null;
  if (boundPlugin) return { hk: boundPlugin };
  try {
    const { registerPlugin } = await import('@capacitor/core');
    boundPlugin = registerPlugin<HealthKitPlugin>('HealthKit');
    return { hk: boundPlugin };
  } catch (err) {
    console.warn('[HealthSync] plugin bind failed:', err instanceof Error ? err.message : String(err));
    return null;
  }
}

async function plugin(): Promise<{ hk: HealthKitPlugin } | null> {
  return getHealthKitPlugin();
}

const SYNC_DAYS = 7;
// Foreground can fire often; the server also rate-limits (health-sync
// 6/min). One sync / 15 min is plenty for daily aggregates.
const MIN_SYNC_INTERVAL_MS = 15 * 60 * 1000;
let lastSyncAt = 0;
let inFlight = false;

/**
 * Pull + upload the last 7 days of HealthKit daily aggregates.
 * Fire-and-forget — resolves silently on every path.
 *
 * @param force skip the throttle (the post-run-save trigger passes true).
 */
export async function syncHealthMetrics(fetchAuthed: AuthFetchFn, force = false): Promise<void> {
  if (inFlight) return;
  const now = Date.now();
  if (!force && now - lastSyncAt < MIN_SYNC_INTERVAL_MS) return;
  const p = await plugin();
  if (!p) return;
  inFlight = true;
  try {
    await p.hk.requestAuthorization();
    const res = await p.hk.queryDailyMetrics({ days: SYNC_DAYS });
    const days = cleanDaysForUpload(res?.days);
    if (days.length === 0) {
      // Nothing to send (no data / all types denied) — count it as a
      // successful pass so the throttle holds.
      lastSyncAt = now;
      return;
    }
    const resp = await fetchAuthed('/api/health-metrics/daily', {
      method: 'POST',
      body: JSON.stringify({ days }),
    });
    if (resp.ok) lastSyncAt = now;
    // 403 (feature off / minor without guardian health_data consent) is
    // an expected background outcome — no surface, and we DON'T advance
    // the throttle so a later consent grant syncs promptly.
    else console.warn('[HealthSync] upload rejected:', resp.status);
  } catch (err) {
    console.warn('[HealthSync] sync failed:', err instanceof Error ? err.message : String(err));
  } finally {
    inFlight = false;
  }
}
